import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { CampaignsActions } from '@actions/index'
import * as fromApp from '@reducers/index'
import { ReviewRequestCampaignModel, RequestModel, LocationModel, BasicReviewRequestCampaignInfo } from '@models/index';
import { HttpApiService } from '@services/http-api.service';
import { MessageService } from 'primeng/api';
import {CampaignState} from '@enums/campaingState.enum';

@Injectable()
export class CampaignEffects {
    searchReviewRequestCampaigns$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CampaignsActions.searchReviewRequestCampaigns),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getCurrentLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `?locationId=${location.id}`
                })
            ),
            switchMap(request =>
                this.httpApiService
                    .getRequest(request)
                    .pipe(
                        map(data => data as ReviewRequestCampaignModel[]),
                        map(data => CampaignsActions.searchReviewRequestCampaignsSuccess({ data })),
                        catchError(error => of(CampaignsActions.campaingError({ error: error.message })))
                    )
            )
        )
    );

    searchReviewRequestCampaignsByLocationId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CampaignsActions.searchReviewRequestCampaignsByLocationId),
            map(action => action.request),
            switchMap(request =>
                this.httpApiService
                    .getRequest(request)
                    .pipe(
                        map(data => data as ReviewRequestCampaignModel[]),
                        map(data => CampaignsActions.searchReviewRequestCampaignsSuccess({ data })),
                        catchError(error => of(CampaignsActions.campaingError({ error: error.message })))
                    )
            )
        )
    );

    searchReviewRequestCampaign$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CampaignsActions.searchReviewRequestCampaign),
            map(action => action.request),
            // withLatestFrom(
            //     this.store.select(fromApp.getCurrentLocation),
            //     (request:RequestModel, location: LocationModel)=>({
            //         ...request,
            //         params: request.params
            //     })
            // ),
            switchMap(request =>
                this.httpApiService
                    .getRequest(request)
                    .pipe(
                        map(data => data as ReviewRequestCampaignModel),
                        map(data => CampaignsActions.searchReviewRequestCampaignSuccess({ data })),
                        catchError(error => of(CampaignsActions.campaingError({ error: error.message })))
                    )
            )
        )
    );

    saveReviewRequestCampaign$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CampaignsActions.saveReviewRequestCampaign),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getCurrentLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    payload: { ...request.payload, locationId: location.id }
                })
            ),
            switchMap(request =>
                this.httpApiService
                    .postRequest(request)
                    .pipe(
                        map(data => data as ReviewRequestCampaignModel),
                        tap(data => this.launchToastMssg('success', 'Section Saved', `Section has been saved`)),
                        map(reviewRequestCampaign => CampaignsActions.saveReviewRequestCampaignSuccess({ reviewRequestCampaign })),
                        catchError(error => of(CampaignsActions.campaingError({ error: error.message })))
                    )
            )
        )
    );

    deleteCampaign$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CampaignsActions.deleteCampaign),
            map(action => action.request),
            switchMap(request =>
                this.httpApiService
                    .postRequest(request)
                    .pipe(
                        map(data => data as { id: string }),
                        tap(data => this.launchToastMssg('success', 'Delete Campaign', 'The campaign has been deleted')),
                        map(data => CampaignsActions.deleteCampaignSuccess({ campaignId: data.id })),
                        catchError(error => of(CampaignsActions.campaingError({ error: error.message })))
                    )
            )
        )
    );

    setCampaignState$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CampaignsActions.setCampaignState),
            map(action => action.request),
            switchMap(request =>
                this.httpApiService
                    .postRequest(request)
                    .pipe(
                        map(data => data as { campaignId: string, editionState: string, state: CampaignState }),
                        tap(data => this.launchToastMssg('success', 'Campaign State', `Campaign has been changed to start ${data.state}.`)),
                        map(data => CampaignsActions.setCampaignStateSuccess({
                            campaignId: data.campaignId,
                            campaignState: data.state,
                            editionState: data.editionState
                        })),
                        catchError(error => of(CampaignsActions.campaingError({ error: error.message })))
                    )
            )
        )
    );

    sendSmsContentTest = createEffect(() =>
        this.actions$.pipe(
            ofType(CampaignsActions.sendSmsContentTest),
            map(action => action.request),
            switchMap(request =>
                this.httpApiService
                    .postRequest(request)
                    .pipe(
                        map(data => data as boolean),
                        map(sendSmsTest => CampaignsActions.sendSmsContentTestSuccess({ sendSmsTest })),
                        tap(data => this.launchToastMssg('success', 'Sending Test', `The SMS test has been sent`)),
                        catchError(error => of(CampaignsActions.campaingError({ error: error.message })))
                    )
            )
        )
    );

    sendEmailTest = createEffect(() =>
        this.actions$.pipe(
            ofType(CampaignsActions.sendEmailTest),
            map(action => action.request),
            switchMap(request =>
                this.httpApiService
                    .postRequest(request)
                    .pipe(
                        map(data => data as boolean),
                        map(sendEmailTest => CampaignsActions.sendEmailTestSuccess({ sendEmailTest })),
                        tap(data => this.launchToastMssg('success', 'Send Test', `The email test has been sent`)),
                        catchError(error => of(CampaignsActions.campaingError({ error: error.message })))
                    )
            )
        )
    );

    getLocationCampaignsBasicInfo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CampaignsActions.getLocationCampaignsBasicInfo),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getCurrentLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `?locationId=${location.id}`
                })
            ),
            switchMap(request =>
                this.httpApiService
                    .getRequest(request)
                    .pipe(
                        map(data => data as BasicReviewRequestCampaignInfo[]),
                        map(data => CampaignsActions.getLocationCampaignsBasicInfoSuccess({ campaignsInfo: data })),
                        catchError(error => of(CampaignsActions.campaingError({ error: error.message })))
                    )
            )
        )
    );

    getLocationCampaignsBasicInfoByLocationId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CampaignsActions.getLocationCampaignsBasicInfoByLocationId),
            map(action => action.request),
            switchMap(request =>
                this.httpApiService
                    .getRequest(request)
                    .pipe(
                        map(data => data as BasicReviewRequestCampaignInfo[]),
                        map(data => CampaignsActions.getLocationCampaignsBasicInfoSuccess({ campaignsInfo: data })),
                        catchError(error => of(CampaignsActions.campaingError({ error: error.message })))
                    )
            )
        )
    );

    addCampaign$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CampaignsActions.addCampaign),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getCurrentLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `?locationId=${location.id}&businessId=${location.businessId}`
                })
            ),
            switchMap(request =>
                this.httpApiService
                    .postRequest(request)
                    .pipe(
                        map(data => data as ReviewRequestCampaignModel),
                        map(data => CampaignsActions.addCampaignSuccess({ data })),
                        catchError(error => {
                            this.launchToastMssg('error', "Error creating campaign", error.error.message);
                            return of(CampaignsActions.campaingError({ error: error.message }))
                        })
                    )
            )
        )
    );

    filterCampaign$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CampaignsActions.filterCampaign),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getCurrentLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `&locationId=${location.id}`
                })
            ),
            switchMap(request =>
                this.httpApiService
                    .getRequest(request)
                    .pipe(
                        map(data => data as ReviewRequestCampaignModel[]),
                        map(data => CampaignsActions.searchReviewRequestCampaignsSuccess({ data })),
                        catchError(error => of(CampaignsActions.campaingError({ error: error.message })))
                    )
            )
        )
    );

    changeLocationPreferredCampaign$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CampaignsActions.changeLocationPreferredCampaign),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getCurrentLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    payload: { ...request.payload, locationId: location.id }
                })
            ),
            switchMap(request =>
                this.httpApiService
                    .postRequest(request)
                    .pipe(
                        map(data => data as LocationModel),
                        map(data => CampaignsActions.changeLocationPreferredCampaignSuccess({ data })),
                        tap(data => this.launchToastMssg('success', 'Preferred Campaign', 'Preferred Campaign set')),
                        catchError(error => of(CampaignsActions.campaingError({ error: error.message })))
                    )
            )
        )
    );

    getLocationCampaignsBasicInfoQuickDot$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CampaignsActions.getLocationCampaignsBasicInfoReports),
            map(action => action.request),
            switchMap(request =>
                this.httpApiService
                    .getRequest(request)
                    .pipe(
                        map(data => data as BasicReviewRequestCampaignInfo[]),
                        map(data => CampaignsActions.getLocationCampaignsBasicInfoReportsSuccess({ campaignsInfo: data })),
                        catchError(error => of(CampaignsActions.campaingError({ error: error.message })))
                    )
            )
        )
    );

    // getLocationCampaignsBasicInfoByLocationId$ = createEffect(() =>
    //     this.actions$.pipe(
    //         ofType(CampaignsActions.getLocationCampaignsBasicInfo),
    //         map(action => action.request),
    //         switchMap(request =>
    //             this.httpApiService
    //                 .getRequest(request)
    //                 .pipe(
    //                     map(data => data as BasicReviewRequestCampaignInfo[]),
    //                     map(data => CampaignsActions.getLocationCampaignsBasicInfoSuccess({ campaignsInfo: data })),
    //                     catchError(error => of(CampaignsActions.campaingError({ error: error.message })))
    //                 )
    //         )
    //     )
    // );

    constructor(
        private actions$: Actions,
        private httpApiService: HttpApiService,
        private messageToast: MessageService,
        private store: Store<any>
    ) { }

    launchToastMssg(severity: string, summary: string, detail: string) {
        this.messageToast.add({ severity: severity, summary: summary, detail: detail, life: 5000 });
    }
}
